import React, { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle, EnvelopeSimple, Eye, EyeSlash, LockKey, PaperPlaneTilt, Spinner } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/errors";

export default function LeaderLogin() {
  const { leader, signInLeaderPassword, requestMagicLink } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const searchParams = new URLSearchParams(loc.search);
  const nextParam = searchParams.get("next");
  const isCoachIntake = nextParam === "/coach";
  const returnTo = nextParam || loc.state?.from || "/start";
  const [mode, setMode] = useState("password"); // password | magic
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [magicSent, setMagicSent] = useState(false);

  if (leader) {
    return <Navigate to={returnTo} replace />;
  }

  const submitPassword = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await signInLeaderPassword(email.trim(), password);
      toast.success("Signed in.");
      nav(returnTo, { replace: true });
    } catch (err) {
      const msg = apiError(err, "Sign-in failed");
      // If the backend tells us this email is actually a coach account,
      // route the user to the coach sign-in page directly.
      if (/coach account/i.test(msg) || /coach\/login/i.test(msg)) {
        toast.error(msg);
        nav(`/coach/login?email=${encodeURIComponent(email.trim())}`);
      } else {
        toast.error(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  const submitMagic = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await requestMagicLink(email.trim());
      setMagicSent(true);
      toast.success("Check your inbox for a sign-in link.");
    } catch (err) {
      toast.error(apiError(err, "Could not send link"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`${isCoachIntake ? "max-w-3xl" : "max-w-md"} mx-auto px-6 sm:px-8 py-16 lg:py-24`}>
      {isCoachIntake && (
        <div className="anim-in delay-1 mb-10" data-testid="coach-intake-welcome">
          <div className="label-kicker" style={{ color: "#1E3A2B" }}>Work with a Coach</div>
          <h1 className="font-serif text-3xl sm:text-4xl text-[#1E3A2B] mt-3 tracking-tight leading-tight">
            Solve a Real Leadership Challenge
          </h1>
          <div className="mt-6 space-y-4 text-[#4A524C] leading-relaxed">
            <p>Bring us a real problem—not a hypothetical one.</p>
            <p>
              Schedule a complimentary 2-hour executive coaching session and
              work with an experienced coach on a leadership challenge you're
              facing right now.
            </p>
            <p>
              Whether it's a difficult employee, a strategic disagreement, a
              stalled negotiation, an organizational conflict, or a complex
              decision, we'll work with you to make meaningful progress on the
              issue that matters most.
            </p>
            <p>
              Briefly describe the challenge you'd like to work on. Before your
              session, your coach will review your situation and prepare to
              help you make the most of your time together.
            </p>
            <p>During your session, you'll:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Clarify what's really driving the problem.</li>
              <li>Explore perspectives and possibilities you may not have considered.</li>
              <li>Generate practical, actionable options for moving forward.</li>
              <li>Leave with greater clarity and a concrete path ahead.</li>
            </ul>
            <p>
              If you find the process valuable, you're welcome to continue with
              executive coaching. If not, you'll leave with new insights and
              practical progress on an important problem—at no cost and with no
              obligation.
            </p>
          </div>
          <div className="mt-8 border-t border-[#E8E4DB]"></div>
        </div>
      )}

      {!isCoachIntake && (
        <div className="anim-in delay-1">
          <div className="label-kicker" style={{ color: "#1E3A2B" }}>Welcome back</div>
          <h1 className="font-serif text-4xl text-[#1E3A2B] mt-3 tracking-tight leading-tight">
            Sign in.
          </h1>
          <p className="text-[#4A524C] mt-3 leading-relaxed">
            See your past conversations and pick up where you left off.
          </p>
        </div>
      )}
      {isCoachIntake && (
        <div className="anim-in delay-2">
          <h2 className="font-serif text-2xl text-[#1E3A2B] tracking-tight">
            Sign in to schedule your session.
          </h2>
          <p className="text-[#4A524C] mt-2 leading-relaxed text-sm">
            New here? <Link to={`/auth/register${nextParam ? `?next=${encodeURIComponent(nextParam)}` : ""}`} className="text-[#1E3A2B] underline">Create a free account →</Link>
          </p>
        </div>
      )}

      <div className="mt-7 inline-flex p-1 bg-[#F3F0EA] rounded-full anim-in delay-2">
        {[
          { v: "password", label: "Password" },
          { v: "magic", label: "Email me a link" },
        ].map((o) => (
          <button
            key={o.v}
            onClick={() => {
              setMode(o.v);
              setMagicSent(false);
            }}
            data-testid={`leader-mode-${o.v}`}
            className={`px-4 py-1.5 rounded-full text-sm transition-all ${
              mode === o.v ? "bg-white text-[#1E3A2B] shadow-sm" : "text-[#8A948E]"
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>

      {magicSent && mode === "magic" ? (
        <div className="mt-8 bg-[#F3F0EA] rounded-2xl p-6 text-center anim-in delay-3" data-testid="magic-sent-banner">
          <PaperPlaneTilt size={28} weight="duotone" className="mx-auto text-[#1E3A2B] mb-3" />
          <div className="font-serif text-xl text-[#1E3A2B]">Check your email.</div>
          <p className="text-sm text-[#4A524C] mt-2 leading-relaxed">
            If an account exists for <strong>{email}</strong>, we just sent a one-click sign-in link. It expires in 20 minutes.
          </p>
          <button
            onClick={() => setMagicSent(false)}
            className="mt-4 text-xs text-[#1E3A2B] underline"
          >
            Use a different email
          </button>
        </div>
      ) : mode === "password" ? (
        <form onSubmit={submitPassword} className="mt-7 space-y-4 anim-in delay-3">
          <label className="block">
            <span className="label-kicker block mb-2">Email</span>
            <div className="relative">
              <EnvelopeSimple size={18} weight="duotone" className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8A948E]" />
              <input
                type="email" required autoFocus autoComplete="email"
                value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="leader-login-email"
                className="w-full bg-white border border-[#E8E4DB] focus:border-[#1E3A2B] focus:ring-2 focus:ring-[#768C7E]/30 focus:outline-none rounded-xl pl-11 pr-4 py-3.5 text-[#121815]"
              />
            </div>
          </label>
          <label className="block">
            <span className="label-kicker block mb-2">Password</span>
            <div className="relative">
              <LockKey size={18} weight="duotone" className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8A948E]" />
              <input
                type={showPw ? "text" : "password"} required autoComplete="current-password"
                value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="leader-login-password"
                className="w-full bg-white border border-[#E8E4DB] focus:border-[#1E3A2B] focus:ring-2 focus:ring-[#768C7E]/30 focus:outline-none rounded-xl pl-11 pr-12 py-3.5 text-[#121815]"
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                data-testid="leader-login-toggle-pw"
                aria-label={showPw ? "Hide password" : "Show password"}
                tabIndex={-1}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-md text-[#8A948E] hover:text-[#1E3A2B] hover:bg-[#F3F0EA] transition-colors"
              >
                {showPw ? <EyeSlash size={18} weight="duotone" /> : <Eye size={18} weight="duotone" />}
              </button>
            </div>
          </label>
          <div className="text-right -mt-2">
            <Link
              to="/auth/reset-request"
              data-testid="leader-forgot-password-link"
              className="text-xs text-[#8A948E] hover:text-[#1E3A2B] underline"
            >
              Forgot your password?
            </Link>
          </div>
          <button
            type="submit" disabled={busy}
            data-testid="leader-login-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-[#1E3A2B] text-white px-5 py-3.5 rounded-xl font-medium hover:bg-[#2C4C3B] disabled:opacity-60 transition-colors"
          >
            {busy ? <Spinner size={18} className="animate-spin" /> : <CheckCircle size={18} weight="duotone" />}
            Sign in
          </button>
          <p className="text-xs text-[#8A948E] text-center pt-2">
            New here? <Link to="/auth/register" className="text-[#1E3A2B] underline">Create an account →</Link>
          </p>
        </form>
      ) : (
        <form onSubmit={submitMagic} className="mt-7 space-y-4 anim-in delay-3">
          <label className="block">
            <span className="label-kicker block mb-2">Email</span>
            <div className="relative">
              <EnvelopeSimple size={18} weight="duotone" className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8A948E]" />
              <input
                type="email" required autoFocus autoComplete="email"
                value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="leader-magic-email"
                className="w-full bg-white border border-[#E8E4DB] focus:border-[#1E3A2B] focus:ring-2 focus:ring-[#768C7E]/30 focus:outline-none rounded-xl pl-11 pr-4 py-3.5 text-[#121815]"
              />
            </div>
          </label>
          <button
            type="submit" disabled={busy}
            data-testid="leader-magic-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-[#1E3A2B] text-white px-5 py-3.5 rounded-xl font-medium hover:bg-[#2C4C3B] disabled:opacity-60 transition-colors"
          >
            {busy ? <Spinner size={18} className="animate-spin" /> : <PaperPlaneTilt size={18} weight="duotone" />}
            Email me a sign-in link
          </button>
        </form>
      )}
    </div>
  );
}
